import type { AudioOutputProvider } from './AudioOutput';
import { SpeechSynthesisAudioOutputProvider } from './SpeechSynthesisAudioOutputProvider';

const DEFAULT_TIMEOUT_MS = 15000;

export interface CloudTextToSpeechAudioOutputProviderOptions {
  apiBaseUrl: string;
  timeoutMs?: number;
  /**
   * Spoken instead whenever the backend TTS call fails for any reason — API
   * key not configured yet, network error, quota exhausted, playback
   * blocked — so a broken/unset backend never means total silence. Pass
   * `null` to disable the fallback and just reject instead. Defaults to the
   * browser-native provider.
   */
  fallbackProvider?: AudioOutputProvider | null;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/** Internal signal for "the in-flight request was abandoned by stop()/dispose()", never a real failure. */
class StoppedSignal extends Error {}

/**
 * Thrown when a chunk fails partway through a multi-chunk cloud utterance,
 * carrying whatever text hadn't been spoken yet so the caller's fallback
 * (browser TTS) can pick up from there instead of repeating already-played
 * audio or losing the message's tail silently.
 */
class PartialTtsFailure extends Error {
  constructor(
    readonly remainingText: string,
    sourceError?: unknown,
  ) {
    super(
      sourceError instanceof Error
        ? sourceError.message
        : 'Text-to-speech failed partway through playback.',
    );
  }
}

const MIN_CHUNK_CHARS = 30;

/**
 * Groups the reply into sentence-sized playback chunks so the first chunk
 * can be fetched, synthesized, and played well before the rest of a long
 * reply is even sent to the TTS backend — instead of one request that waits
 * for the whole reply to be synthesized before any sound plays. Tiny
 * fragments (e.g. "Yes.") are merged into the following sentence so we don't
 * fire a full network round trip for a couple of words.
 */
function splitIntoChunks(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const sentences = trimmed.match(/[^.!?]+[.!?]+[)\]'"`’”]*(?:\s+|$)|[^.!?]+$/g) ?? [trimmed];

  const chunks: string[] = [];
  let buffer = '';
  for (const rawSentence of sentences) {
    const sentence = rawSentence.trim();
    if (!sentence) continue;
    buffer = buffer ? `${buffer} ${sentence}` : sentence;
    if (buffer.length >= MIN_CHUNK_CHARS) {
      chunks.push(buffer);
      buffer = '';
    }
  }
  if (buffer) chunks.push(buffer);
  return chunks;
}

/**
 * A minimal valid 1-sample, 8-bit mono PCM WAV, built byte-for-byte (not a
 * hand-typed base64 constant) so there's no risk of a corrupt/invalid data
 * URI. Used only to "prime" <audio> playback during a real user gesture —
 * required on iOS Safari before any later programmatic .play() is allowed —
 * never actually audible.
 */
function createSilentWavUrl(): string {
  const sampleRate = 8000;
  const dataSize = 1;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeString = (offset: number, text: string): void => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate, true); // byte rate (sampleRate * blockAlign(1) * bitsPerSample(8)/8)
  view.setUint16(32, 1, true); // block align
  view.setUint16(34, 8, true); // bits per sample
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);
  view.setUint8(44, 128); // one silent 8-bit unsigned PCM sample (128 = zero amplitude)
  return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
}

/**
 * TTS provider backed by our backend's Cloud Text-to-Speech proxy (see
 * server/src/tts): the backend picks one consistent (female) voice per
 * language from Google's full voice catalog, so every language is heard in
 * the same voice identity instead of whatever mixed-gender default the
 * browser happens to have installed per locale (the limit
 * SpeechSynthesisAudioOutputProvider alone can't get past).
 *
 * Implements the same AudioOutputProvider contract as that browser-native
 * provider, so it's a drop-in default — no other SDK/AudioOutput change
 * needed — and uses that same provider as its own fallback when the
 * backend call fails, so speech is never silently lost, only temporarily
 * lower/per-locale-default quality until the backend TTS is configured.
 */
export class CloudTextToSpeechAudioOutputProvider implements AudioOutputProvider {
  private readonly fallback: AudioOutputProvider | null;
  private audio: HTMLAudioElement | null = null;
  private readonly objectUrls = new Set<string>();
  private readonly activeControllers = new Set<AbortController>();
  private stopped = false;
  /** Settles the pending speak() promise directly — pausing an <audio> element fires no event of its own to hook into. */
  private resolvePlayback: (() => void) | null = null;

  constructor(private readonly options: CloudTextToSpeechAudioOutputProviderOptions) {
    this.fallback =
      options.fallbackProvider === null
        ? null
        : (options.fallbackProvider ?? new SpeechSynthesisAudioOutputProvider());
  }

  async speak(text: string, language: string | null = null): Promise<void> {
    // Never let two utterances overlap, regardless of caller discipline —
    // mirrors SpeechSynthesisAudioOutputProvider's own guarantee. Marking
    // `stopped` around this stop is what makes the *previous* call's own
    // in-flight request/playback resolve cleanly instead of surfacing as a
    // failure once it notices it was cut short.
    this.stopped = true;
    this.stopPlayback();
    this.stopped = false;

    try {
      await this.speakViaCloud(text, language);
    } catch (error) {
      if (this.stopped) return; // a deliberate stop()/dispose() mid-request, not a real failure
      if (!this.fallback) throw error;
      // A chunk that already played shouldn't be repeated by the fallback —
      // only speak whatever text the cloud provider never got to.
      const fallbackText = error instanceof PartialTtsFailure ? error.remainingText : text;
      if (!fallbackText.trim()) return;
      await this.fallback.speak(fallbackText, language);
    }
  }

  stop(): void {
    this.stopped = true;
    this.stopPlayback();
    this.fallback?.stop();
  }

  dispose(): void {
    this.stop();
    this.fallback?.dispose();
  }

  /**
   * iOS Safari refuses to .play() an <audio> element that wasn't triggered
   * by (or tied closely enough to) a user gesture — silently, no error.
   * This SDK's auto-play flow calls speak() only after a full async round
   * trip (record -> upload -> Gemini -> TTS fetch), well outside that
   * window, so without this the first (and every) reply would just produce
   * no sound at all on iOS. Priming a real, if silent, playback here — from
   * directly inside a user gesture, before any `await` — is what unlocks
   * later programmatic .play() calls for the rest of the page. Also primes
   * the fallback provider, in case the cloud call fails later and control
   * passes to it instead.
   */
  unlock(): void {
    this.fallback?.unlock?.();
    try {
      const url = createSilentWavUrl();
      const audio = new Audio(url);
      audio.volume = 0;
      const cleanup = (): void => URL.revokeObjectURL(url);
      const playResult = audio.play();
      if (playResult && typeof playResult.then === 'function') {
        playResult
          .then(() => {
            audio.pause();
            cleanup();
          })
          .catch(cleanup);
      } else {
        cleanup();
      }
    } catch {
      // Best-effort only — a failed unlock just means iOS may block the
      // first real reply, exactly as if this method didn't exist.
    }
  }

  private stopPlayback(): void {
    for (const controller of this.activeControllers) controller.abort();
    this.activeControllers.clear();
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio = null;
    }
    for (const url of this.objectUrls) URL.revokeObjectURL(url);
    this.objectUrls.clear();
    if (this.resolvePlayback) {
      const resolve = this.resolvePlayback;
      this.resolvePlayback = null;
      resolve();
    }
  }

  /**
   * Speaks the reply chunk-by-chunk instead of one request for the whole
   * text: chunk 0 starts playing as soon as it's back from the backend,
   * while chunk 1 is already being fetched in the background, so playback
   * never stalls waiting on the network once it's begun. This is what turns
   * "wait for the full synthesized reply, then play it" into "start talking
   * almost immediately."
   */
  private async speakViaCloud(text: string, language: string | null): Promise<void> {
    const chunks = splitIntoChunks(text);
    if (chunks.length === 0) return;

    let nextChunk: Promise<{ url: string; mimeType: string }> | null = this.fetchChunkAudio(
      chunks[0]!,
      language,
    );

    for (let i = 0; i < chunks.length; i += 1) {
      if (this.stopped || !nextChunk) return;

      let current: { url: string; mimeType: string };
      try {
        current = await nextChunk;
      } catch (error) {
        if (error instanceof StoppedSignal) return;
        throw new PartialTtsFailure(chunks.slice(i).join(' '), error);
      }

      if (this.stopped) return;

      // Kick off the next chunk's synthesis now, in parallel with this
      // chunk's playback, instead of after it finishes. The real handling
      // happens via `await nextChunk` on the next iteration; this no-op
      // catch only stops it counting as an unhandled rejection if it
      // settles (as it may, e.g. on a network error) before that await is
      // reached.
      nextChunk = i + 1 < chunks.length ? this.fetchChunkAudio(chunks[i + 1]!, language) : null;
      nextChunk?.catch(() => {});

      try {
        await this.playChunk(current.url);
      } catch (error) {
        if (this.stopped) return;
        throw new PartialTtsFailure(chunks.slice(i + 1).join(' '), error);
      } finally {
        this.objectUrls.delete(current.url);
        URL.revokeObjectURL(current.url);
      }
    }
  }

  private fetchChunkAudio(
    text: string,
    language: string | null,
  ): Promise<{ url: string; mimeType: string }> {
    const controller = new AbortController();
    this.activeControllers.add(controller);
    const timer = setTimeout(
      () => controller.abort(),
      this.options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    );

    return (async (): Promise<{ url: string; mimeType: string }> => {
      try {
        let res: Response;
        try {
          res = await fetch(`${this.options.apiBaseUrl}/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, language: language ?? undefined }),
            signal: controller.signal,
          });
        } catch {
          if (this.stopped) throw new StoppedSignal();
          throw new Error('Could not reach the text-to-speech backend.');
        }

        if (this.stopped) throw new StoppedSignal();

        if (!res.ok) {
          const payload = (await res.json().catch(() => null)) as {
            error?: { message?: string };
          } | null;
          throw new Error(payload?.error?.message ?? 'Text-to-speech request failed.');
        }

        const payload = (await res.json()) as { audioContent?: string; mimeType?: string };
        if (!payload.audioContent) {
          throw new Error('Text-to-speech response had no audio.');
        }
        if (this.stopped) throw new StoppedSignal();

        const mimeType = payload.mimeType ?? 'audio/mpeg';
        const blob = base64ToBlob(payload.audioContent, mimeType);
        const url = URL.createObjectURL(blob);
        this.objectUrls.add(url);
        return { url, mimeType };
      } finally {
        clearTimeout(timer);
        this.activeControllers.delete(controller);
      }
    })();
  }

  private playChunk(url: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const audio = new Audio(url);
      this.audio = audio;
      this.resolvePlayback = () => {
        this.resolvePlayback = null;
        resolve();
      };
      audio.onended = () => this.resolvePlayback?.();
      audio.onerror = () => {
        if (this.stopped) {
          this.resolvePlayback?.();
          return;
        }
        this.resolvePlayback = null;
        reject(new Error('Audio playback failed.'));
      };
      audio.play().catch((error: unknown) => {
        if (this.stopped) {
          this.resolvePlayback?.();
          return;
        }
        this.resolvePlayback = null;
        reject(error instanceof Error ? error : new Error('Audio playback failed.'));
      });
    });
  }
}
